import { Box, Stack, Tooltip, Typography, type TypographyProps } from '@mui/material';
import { useState, type ReactNode } from 'react';
import { fontFamilies } from '@/theme/tokens';

/**
 * Shared cell renderers, so a name, an identifier and a figure look the same in
 * every grid. Monospace is reserved for true identifiers — codes, ids, tokens —
 * rather than used as a decorative texture.
 */

/**
 * One line of a cell that is cut with an ellipsis when the column is too
 * narrow, and then — only then — says the whole of itself in a tooltip. A
 * column that flexes narrower than its longest value is how a list fits its
 * panel at 1280px; the tooltip is what makes that cut harmless.
 */
export function TruncatedText({ children, sx, ...props }: TypographyProps) {
  const [cut, setCut] = useState(false);
  return (
    <Tooltip title={cut ? children : ''} placement="top-start">
      <Typography
        noWrap
        {...props}
        sx={{ minWidth: 0, maxWidth: '100%', ...sx }}
        onPointerEnter={(event) => {
          const el = event.currentTarget;
          setCut(el.scrollWidth > el.clientWidth + 1);
        }}
      >
        {children}
      </Typography>
    </Tooltip>
  );
}

/** A record's name with its secondary identifier beneath. */
export function PrimaryCell({ title, meta }: { title?: ReactNode; meta?: ReactNode }) {
  // Line heights are pinned rather than inherited: two stacked lines at the
  // body defaults come to 50px, which all but fills a 52px row.
  return (
    <Stack sx={{ minWidth: 0, width: '100%' }} justifyContent="center">
      <TruncatedText variant="body2" fontWeight={600} sx={{ lineHeight: 1.35 }}>
        {title || '—'}
      </TruncatedText>
      {meta ? (
        <TruncatedText sx={{ fontSize: '0.75rem', lineHeight: 1.3, color: 'text.disabled' }}>
          {meta}
        </TruncatedText>
      ) : null}
    </Stack>
  );
}

/** Plain secondary text: dates, phone numbers, short values. */
export function MonoCell({ value, dim }: { value?: ReactNode; dim?: boolean }) {
  return (
    <TruncatedText sx={{ fontSize: '0.875rem', color: dim ? 'text.disabled' : 'text.primary' }}>
      {value ?? '—'}
    </TruncatedText>
  );
}

/**
 * A value that is always read left to right, whatever the page's direction, and
 * is **not** a phone number — a coordinate pair, a version, a range. The phone
 * has its own component, `PhoneChip`, because the ruling gives it a box as
 * well; this is the protection on its own, for a value a box would over-dress.
 *
 * It matters wherever the parts carry different meanings: `33.31672, 44.33977`
 * in an Arabic page renders as `44.33977 ,33.31672`, so the reader takes the
 * longitude for the latitude. Found by measuring, 2026-10-05.
 */
export function LtrValue({ children }: { children: ReactNode }) {
  return (
    <Box component="span" dir="ltr" sx={{ unicodeBidi: 'isolate' }}>
      {children}
    </Box>
  );
}

/** Identifiers only — where character-by-character comparison is the point. */
export function CodeCell({ value }: { value?: ReactNode }) {
  return (
    <TruncatedText sx={{ fontFamily: fontFamilies.mono, fontSize: '0.75rem', color: 'text.disabled' }}>
      {value ?? '—'}
    </TruncatedText>
  );
}

/** Right-aligned figure, for columns that are summed or compared. */
export function NumberCell({ value, tone }: { value?: number | null; tone?: 'default' | 'muted' }) {
  return (
    <Box sx={{ width: '100%', textAlign: 'end', lineHeight: 1 }}>
      <Typography
        component="span"
        sx={{
          fontSize: '0.875rem',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 550,
          color: tone === 'muted' ? 'text.disabled' : 'text.primary',
        }}
      >
        {value ?? 0}
      </Typography>
    </Box>
  );
}
